/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Chart } from '@antv/g2';
import DataSet from '@antv/data-set';
import { GetIcon } from 'datastake-daf/dist/components';
import i18next from 'i18next';
import styled from 'styled-components';
import { Popover } from 'antd';

const icons = [
  {
    className: 'icon-a',
    icon: (
      <GetIcon icon="auction-line" />
    ),
    tooltip: 'Legality',
  },
  {
    className: 'icon-b',
    icon: (
      <GetIcon icon="open-arm-line" />
    ),
    tooltip: 'Human Rights',
  },
  {
    className: 'icon-c',
    icon: (
      <GetIcon icon="fire-line" />
    ),
    tooltip: 'Conflict & Security',
  },
  {
    className: 'icon-d',
    icon: (
      <GetIcon icon="team-line" />
    ),
    tooltip: 'Environment & Natural Resources',
  },
  {
    className: 'icon-e',
    icon: (
      <GetIcon icon="leaf-line" />
    ),
    tooltip: 'Health & Safety',
  },
  {
    className: 'icon-f',
    icon: (
      <GetIcon icon="first-aid-kit-line" />
    ),
    tooltip: 'Community & Society',
  },
];

const Style = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  overflow: hidden;
  position: relative;
  max-width: 100%;

  i {
    font-size: 18px;
    color: var(--mmt-primary-70);
  }

  .icon-a {
    position: absolute;
    top: 6%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 99;
  }
  
  .icon-b {
    position: absolute;
    top: 28%;
    right: 7%;
    transform: translate(40%, -50%);
    z-index: 99;
  }

  .icon-c {
    position: absolute;
    bottom: 26%;
    right: 7%;
    transform: translate(40%, -50%);
    z-index: 99;
  }

  .icon-d {
    position: absolute;
    bottom: 3%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 99;
  }

  .icon-e {
    position: absolute;
    bottom: 26%;
    left: 7%;
    transform: translate(-50%, -50%);
    z-index: 99;
  }

  .icon-f {
    position: absolute;
    top: 28%;
    left: 7%;
    transform: translate(-50%, -50%);
    z-index: 99;
  }
`

export default function RiskProfileIcons({
  data = [],
  language,
  defaultFields = null,
  cont,
}) {
  const container = useRef();
  const [graphData, setGraphData] = useState(data);
  const [mainGraph, setMainGraph] = useState(null);
  const [graphWidth, setGraphWidth] = useState(0);
  const [graphHeight, setGraphHeight] = useState(0);

  const observer = useRef(new ResizeObserver((entries) => {
    window.requestAnimationFrame(() => {
      const { width, height } = entries[0].contentRect;
      setGraphWidth(width);
      setGraphHeight(height);
    });
  }));

  useEffect(() => {
    if (mainGraph && (mainGraph.width !== graphWidth || mainGraph.height !== graphHeight) && graphWidth && graphHeight) {
      let gSize = graphWidth;
      if (gSize > 360) {
        gSize = 360;
      }
      let gHeight = gSize;
      mainGraph.changeSize(gSize, gHeight);

      mainGraph.axis('item', {
        line: null,
        tickLine: null,
        label: {
          autoHide: true,
          style: {
            fontSize: 0,
          },
        },
        grid: {
          line: {
            style: {
              lineDash: null,
            },
          },
        },
      });
      mainGraph.axis('score', {
        line: null,
        tickLine: null,
        label: {
          autoHide: true,
          style: {
            fontSize: 12,
          },
        },
        grid: {
          line: {
            type: 'line',
            style: {
              lineDash: null,
            },
          },
        },
      });
      mainGraph.render(true);
    }
  }, [graphWidth, graphHeight, mainGraph]);

  useEffect(() => {
    setGraphData(data);
  }, [data, language]);
  useEffect(() => {
    if (container.current) {
      observer.current.observe(container.current);
    }
    return () => {
      if (container.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.current.unobserve(container.current);
      }
    }
  }, [container.current, observer]);

  const getHeight = () => {
    return container.current.offsetHeight
  };

  useEffect(() => {
    if (container.current) {
      const { DataView } = DataSet;
      const dv = new DataView().source(data);
      dv.transform({
        type: 'fold',
        fields: defaultFields || [i18next.t('Baseline'), i18next.t('Current')],
        key: 'user',
        value: 'score',
      });
      if (mainGraph) {
        mainGraph.data(dv.rows);
        mainGraph.render(true);
      } else {
        const chart = new Chart({
          container: container.current,
          // autoFit: true,
          height: getHeight(),
          width: container.current.offsetWidth,
          // renderer: 'svg',
        });
        chart.data(dv.rows);
        chart.scale('score', {
          min: 0,
          max: 5,
          showLast: false,
          sync: true,
        });
        chart.coordinate('polar', {
          radius: 0.8,
        });
        chart.tooltip({
          shared: true,
          showCrosshairs: true,
          crosshairs: {
            line: {
              style: {
                lineDash: [4, 4],
                stroke: '#333'
              }
            }
          },
          customContent: (title, data) => {
            let content = '';
            let t = '';
            if (data && data.length) {
              t = data[0].data.label;
              content = '<ul class="g2-tooltip-list"';
              data.forEach(v => {
                content += `<li class="g2-tooltip-list-item" data-index="" style="list-style-type: none; padding: 0px; margin: 12px 0px;">
                    <span class="g2-tooltip-marker" style="background: ${v.mappingData.color}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 8px;"></span>
                    <span class="g2-tooltip-name">${v.name}</span>
                    <span class="g2-tooltip-value" style="display: inline-block; float: right; margin-left: 30px;">${v.value ? Number(v.value).toFixed(2) : '-'}</span>
                  </li>`;
              });
              content += '</ul>';
            }
            return `<div>
              ${t ? `<div class="g2-tooltip-title" style="margin-bottom: 12px; margin-top: 12px;">${(t || '')}</div>` : ''}
              ${content}
            </div>`;
          }
        });
        chart.axis('item', {
          line: null,
          tickLine: null,
          label: {
            autoHide: true,
            style: {
              fontSize: 10,
            },
          },
          grid: {
            line: {
              style: {
                lineDash: null,
              },
            },
          },
        });
        chart.axis('score', {
          line: null,
          tickLine: null,
          label: {
            autoHide: true,
            style: {
              fontSize: 12,
            },
          },
          grid: {
            line: {
              type: 'line',
              style: {
                lineDash: null,
              },
            },
          },
        });
        chart.legend(false);
        chart.annotation(false)
        chart
          .line()
          .position('item*score')
          .color('user')
          .size(2);
        chart
          .point()
          .position('item*score')
          .shape('circle')
          .color('user')
          .size(3)
          .style({
            stroke: '#fff',
            lineWidth: 1,
            fillOpacity: 1,
          });
        chart
          .area()
          .position('item*score')
          .color('user');
        chart.render();
        setMainGraph(chart);
      }
    }
  }, [container, graphData])

  return (
    <Style
      ref={container}
      className="riskProfile"
      style={{
        width: graphWidth ? graphWidth > 360 ? 360 : graphWidth : undefined,
        height: graphWidth ? graphWidth > 360 ? 400 : graphWidth + 40 : undefined,
        marginTop: graphWidth ? 'auto' : undefined,
        marginBottom: graphWidth ? 'auto' : undefined,
        flex: graphWidth ? 'unset' : 1,
      }}
    >
      {icons.map((i, j) => (
        <Popover
          key={`${i.className}-${i.tooltip}-${j}`}
          content={i18next.t(i.tooltip)}
          placement="bottom"
        >
          <div className={i.className}
            key={`icons-${i.className}`}>
            {i.icon}
          </div>
        </Popover>
      ))}
    </Style>
  );
}

